import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import settings from '../core/settings';

const DEFAULT_INSTRUCTIONS = `How to Play:\nUse your mouse to click and drag a rectangle around a group of tiles adding to 10.\nEach tile cleared is one point.\nTry to score as many points as you can!`;

function InstructionsBox({ usePortal = false, parentEl = null, maxWidth = 540, visible: controlledVisible, onClose, text, settingsKey }) {
  const key = settingsKey || settings.KEYS.SHOW_INSTRUCTIONS;
  const [internalVisible, setInternalVisible] = useState(() => settings.getBool(key));
  const isControlled = typeof controlledVisible === 'boolean';
  const visible = isControlled ? controlledVisible : internalVisible;

  useEffect(() => {
    const onSettingsChanged = (ev) => {
      try {
        if (ev && ev.detail && ev.detail.key === key) {
          const raw = ev.detail.value === undefined ? settings.getBool(key) : ev.detail.value;
          const parsed = typeof raw === 'string' ? raw === 'true' : Boolean(raw);
          if (!isControlled) setInternalVisible(parsed);
        }
      } catch (e) {}
    };
    window.addEventListener('settingsChanged', onSettingsChanged);
    // also listen to storage for other tabs
    const onStorage = (e) => {
      if (e.key === key) {
        if (!isControlled) setInternalVisible(e.newValue === 'true');
        else if (typeof onClose === 'function' && e.newValue === 'false') onClose();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('settingsChanged', onSettingsChanged);
      window.removeEventListener('storage', onStorage);
    };
  }, [key, isControlled, onClose]);

  const close = () => {
    try { settings.set(key, false); } catch (e) {}
    if (!isControlled) setInternalVisible(false);
    if (typeof onClose === 'function') onClose();
  };

  if (!visible) return null;

  const boxStyle = {
    position: parentEl ? 'absolute' : 'fixed',
    left: '50%',
    transform: parentEl ? 'translate(-50%, 12px)' : 'translateX(-50%)',
    top: parentEl ? '100%' : undefined,
    bottom: parentEl ? undefined : 24,
    maxWidth: maxWidth,
    width: '90%',
    background: 'rgba(44,47,52,0.9)',
    color: 'white',
    opacity: 1,
    padding: '10px 14px',
    borderRadius: 10,
    border: '1px solid rgba(255,255,255,0.06)',
    zIndex: 9999,
    fontSize: '0.9rem',
    lineHeight: '1.45',
    textAlign: 'left',
    whiteSpace: 'pre-wrap',
    fontFamily: 'inherit',
    display: 'flex',
    gap: 8,
    alignItems: 'flex-start',
  };

  const content = (
    <div role="note" aria-label="How to play instructions" style={boxStyle}>
      <div style={{ flex: 1 }}>{text ?? DEFAULT_INSTRUCTIONS}</div>
      <button
        onClick={close}
        aria-label="Close instructions"
        style={{
          background: 'transparent',
          border: 'none',
          color: 'rgba(255,255,255,0.85)',
          fontSize: 16,
          lineHeight: 1,
          cursor: 'pointer',
          padding: 4,
        }}
      >
        ×
      </button>
    </div>
  );

  if (usePortal && typeof document !== 'undefined') {
    try {
      const target = parentEl || document.body;
      return ReactDOM.createPortal(content, target);
    } catch (e) {
      return content;
    }
  }

  return content;
}

export default InstructionsBox;
